import {useForwardingRef} from '@rocket-ui/react-tools';
import React, {useCallback} from 'react';
import {InputProps} from '../types';

type UsePrefixProps = InputProps & {
	ref?: React.Ref<HTMLInputElement>;
	prefix: string;
};

export function usePrefix({
	ref: inputRef,
	value: inputValue,
	prefix,
	onChange,
	placeholder: inputPlaceholder,
	dynamicPlaceholder: inputDynamicPlaceholder,
	...props
}: UsePrefixProps) {
	const ref = useForwardingRef(inputRef);

	const handleChange: InputProps['onChange'] = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			event.target.value = event.target.value.trim().replace(prefix, '').trim();

			onChange?.(event);
		},
		[onChange, prefix],
	);

	const value = `${prefix} ${inputValue ?? ''}`;

	const placeholder = useCallback(() => {
		const pl = typeof inputPlaceholder === 'function' ? inputPlaceholder({value: inputValue}) : inputPlaceholder;
		return inputValue === '' && pl ? `${prefix} ${pl}` : undefined;
	}, [inputPlaceholder, inputValue, prefix]);

	const dynamicPlaceholder = useCallback(
		() =>
			inputDynamicPlaceholder
				? typeof inputDynamicPlaceholder === 'function'
					? inputDynamicPlaceholder({value: inputValue})
					: `${value}${inputValue ? inputDynamicPlaceholder.slice(String(inputValue).length) : inputDynamicPlaceholder}`
				: undefined,
		[inputValue, value, inputDynamicPlaceholder],
	);

	return {...props, ref, value, onChange: handleChange, placeholder, dynamicPlaceholder};
}
